import { useEffect, useRef } from 'react';

import { di } from 'react-magnetic-di';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

type RulePlanAnalyticsProps = {
	actionRenderer: ActionRendererFor<typeof ActionKeys.StudioAutomationCreateRule>;
};

/**
 * Tracks rule plan generated and viewed events.
 *
 * - `rule createFlowPlanViewed` fires on every render (including historical re-views).
 * - `rule createFlowPlanGenerated` fires only for non-historical messages.
 */
export const RulePlanAnalytics = ({ actionRenderer }: RulePlanAnalyticsProps) => {
	di(useAnalytics);

	const { conversationId, messageId, isHistoricalMessage } = actionRenderer;
	const firstAction = actionRenderer.actions.at(0);
	const { invocationId } = firstAction ?? {};
	const actionRef = useRef(firstAction);
	const isHistoricalMessageRef = useRef(isHistoricalMessage);
	isHistoricalMessageRef.current = isHistoricalMessage;

	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		const action = actionRef.current;

		if (!action) {
			return;
		}

		const attributes = {
			conversationId,
			invocationId,
			messageId,
			actionKey: action.key,
		};

		// https://data-portal.internal.atlassian.com/analytics/registry/104714
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'createFlowPlanViewed',
			actionSubject: 'rule',
			attributes,
		});

		// https://data-portal.internal.atlassian.com/analytics/registry/104712
		if (!isHistoricalMessageRef.current) {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'createFlowPlanGenerated',
				actionSubject: 'rule',
				attributes,
			});
		}
	}, [sendAnalyticsEvent, conversationId, invocationId, messageId]);

	return null;
};
